import type { InputHTMLAttributes, ReactNode } from "react";

import { cn } from "../lib/cn.js";
import { fieldClassName } from "./TextField.js";

/**
 * Entry of a EUR amount.
 *
 * `type="text"` with `inputMode="decimal"`, NOT `type="number"`. That looks like the obvious choice
 * and is a trap for money:
 *
 *   - the spinner arrows invite nudging a balance by one euro, which is never what anyone wants;
 *   - a scroll wheel over a focused number input silently changes the value;
 *   - browsers accept `1e5` and `+-3` as valid numeric input;
 *   - and the decimal separator follows the BROWSER's locale rather than the document's, so a Dutch
 *     household typing "12,50" into a browser set to en-US gets an empty value with no explanation.
 *
 * `inputMode="decimal"` gives a phone the numeric keypad, which is the only part of `type="number"`
 * actually wanted here.
 *
 * BOTH SEPARATORS ARE ACCEPTED. `formatEur` renders with a comma because the household is Dutch, so
 * a member reading "12,50" on screen will type a comma back. A member with an en-US keyboard habit
 * will type a dot. Rejecting either would be pedantry about a difference nobody intends.
 *
 * SO ARE THOUSANDS SEPARATORS, as of feature 006.
 *
 * This originally accepted a single separator, as the decimal mark. The consequence only became
 * visible when savings put real amounts on screen: `formatEur` renders "€ 1.234,56", and typing that
 * back into the field did nothing at all. Every keystroke after the first dot was silently dropped,
 * with no message, because rejecting rather than sanitising is what this control does. An interface
 * that refuses to accept its own output is a bug however defensible each half is on its own.
 */

/**
 * Optional minus, then digits in groups of at most three separated by dots or commas.
 *
 * Deliberately more permissive than what `parseEurInput` will accept, because this runs on EVERY
 * KEYSTROKE and a half-typed amount passes through states a finished one never does — "1.", "1.2"
 * and "1.23" are all on the way to "1.234,56". Anything this admits and the parser rejects simply
 * fails to submit, which is recoverable; anything this rejects cannot be typed at all, which is not.
 *
 * Only the LAST group may be empty, which is what allows "1," — the separator arrives before the
 * digits that follow it. Every earlier group needs at least one digit, so "1,,2" is still refused
 * rather than left for the parser: it is not a state anything valid passes through.
 *
 * ==========================================================================================
 * THE TRAILING GROUP'S WIDTH IS THE `scale`, AND IT IS WHY THIS IS BUILT
 * ==========================================================================================
 *
 * Money is two decimals, so the trailing group admits at most three digits: two plus the ambiguous
 * three-digit case that `parseEurInput` reads as grouping. A price per share is FOUR — `PricePerShare`
 * holds ten-thousandths of a euro because `price_per_share` is the one `numeric(15,4)` money column
 * in the schema, and routed through integer cents a €0.0123 penny stock becomes €0.01, a 19% error
 * in the figure a whole position is computed from.
 *
 * So a fixed two-decimal control CANNOT be used for a price: the fourth keystroke would simply not
 * register, and the field would silently refuse a legitimate figure. That is the trap this parameter
 * exists to avoid, and it is why investments' fields were migrated to `DecimalInput` rather than to
 * `MoneyInput`.
 */
const decimalPattern = (scale: number) =>
  new RegExp(`^-?\\d*(?:[.,]\\d{1,3})*(?:[.,]\\d{0,${String(Math.max(scale, 3))}})?$`);

export interface DecimalInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "type"
> {
  /** The raw text, held by the caller. Not a number — see below. */
  value: string;
  /** Called only when the new text is a valid partial amount, so invalid keystrokes never land. */
  onValueChange: (value: string) => void;
  /**
   * How many digits may follow the decimal separator. Default 2 — money.
   *
   * `4` for the two quantities in GLADIS that are not money at this scale: a price per share, which
   * the schema stores as `numeric(15,4)`, and a share quantity, which is an exact count of
   * ten-thousandths. See the pattern's own note for what a fixed 2 would cost.
   */
  scale?: number;
}

export type MoneyInputProps = Omit<DecimalInputProps, "scale">;

/**
 * The value is a STRING the caller owns, not a number.
 *
 * A number cannot represent the states a half-typed amount passes through — "", "-", "12." and
 * "12,5" are all legitimate mid-entry and none is a number. Controlling this with a number forces a
 * parse on every keystroke, and the field then fights the person typing: it erases the separator
 * they just pressed, or rewrites "12,50" to "12.5" under the cursor. Parse once, on submit.
 */
export function DecimalInput({
  value,
  onValueChange,
  scale = 2,
  className,
  ...rest
}: DecimalInputProps): ReactNode {
  const pattern = decimalPattern(scale);
  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={value}
      onChange={(event) => {
        const next = event.target.value;
        // Reject rather than sanitise. Silently rewriting what someone typed is more confusing than
        // the keystroke simply not registering, and a digit past the scale is always a mistake.
        if (pattern.test(next)) onValueChange(next);
      }}
      className={cn(fieldClassName, "text-right tabular-nums", className)}
      {...rest}
    />
  );
}

/**
 * Entry of a EUR amount — `DecimalInput` at two decimal places, which is the overwhelming majority
 * of the money fields in this application.
 *
 * IT KEEPS ITS OWN NAME rather than every caller writing `<DecimalInput scale={2}>`. Fourteen forms
 * collect money and two collect something else; naming the common case is what stops the two
 * exceptions being copied into the twelve by somebody reaching for the nearest example.
 */
export function MoneyInput(props: MoneyInputProps): ReactNode {
  return <DecimalInput {...props} scale={2} />;
}

/**
 * Turns what `MoneyInput` holds into a number, or `null` if it is not a complete amount.
 *
 * Exported because every caller needs it on submit, and each one writing its own `parseFloat` with
 * its own comma handling is how "12,50" becomes 12 in one place and 1250 in another.
 *
 * THE RULE, WHICH RESOLVES BOTH LOCALES AT ONCE: the LAST separator is the decimal mark, and every
 * separator before it is grouping. So "1.234,56" and "1,234.56" are both 1234.56, "12,50" and
 * "12.50" are both 12.5, and neither locale has to be detected or configured.
 *
 * ONE CASE IS GENUINELY AMBIGUOUS and is worth stating rather than hiding: a single separator
 * followed by exactly three digits. "1.234" is 1234 to a Dutch reader and 1.234 to an English one.
 * It is read as GROUPING, because that is the only reading that yields a valid amount — three
 * decimal places is not a euro figure, so the alternative is not "1.234" but `null`.
 */
export function parseEurInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "-") return null;

  const negative = trimmed.startsWith("-");
  const digitsAndSeparators = negative ? trimmed.slice(1) : trimmed;
  if (!/^\d[\d.,]*$/.test(digitsAndSeparators)) return null;

  const lastSeparator = Math.max(
    digitsAndSeparators.lastIndexOf("."),
    digitsAndSeparators.lastIndexOf(","),
  );

  let whole: string;
  let fraction: string;

  if (lastSeparator === -1) {
    whole = digitsAndSeparators;
    fraction = "";
  } else {
    const after = digitsAndSeparators.slice(lastSeparator + 1);
    // Exactly three digits after the final separator: grouping, per the note above. Anything else
    // is the decimal part, and more than two digits of it is not an amount.
    if (after.length === 3) {
      whole = digitsAndSeparators;
      fraction = "";
    } else {
      if (after.length === 0 || after.length > 2) return null;
      whole = digitsAndSeparators.slice(0, lastSeparator);
      fraction = after;
    }
  }

  /*
   * Whatever separators remain in the whole part are grouping — and grouping has a SHAPE.
   *
   * Checked rather than merely stripped: "12.34.56" is somebody's typo, not a number, and reading
   * it as 1234.56 would put a silently wrong amount into a financial record. Refusing it stops the
   * form instead. Groups are 1–3 digits first, exactly 3 thereafter, which is what every locale
   * that groups at all agrees on.
   *
   * UNGROUPED DIGITS ARE THE COMMON CASE and pass first. Most people type "5600", not "5.600", and
   * an earlier draft of this check demanded grouping of everything — which rejected every plain
   * amount over three digits. The unit tests missed it because they all used either small values or
   * grouped ones; the end-to-end journey caught it on the first account it tried to create.
   */
  if (!/^\d+$/.test(whole) && !/^\d{1,3}(?:[.,]\d{3})*$/.test(whole)) return null;

  const wholeDigits = whole.replace(/[.,]/g, "");
  if (wholeDigits === "") return null;

  const amount = Number(`${wholeDigits}.${fraction || "0"}`);
  if (!Number.isFinite(amount)) return null;
  return negative ? -amount : amount;
}
